'use client';

import type { Blog, BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';

import { useId, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

import { ArticleCard } from './article-card';
import {
  blogListInitialData,
  blogListKey,
  blogListRequest,
  nextBlogPage,
  type BlogListPage,
} from './blog-list-query';

/**
 * Zoznam článkov s filtrom kategórií — Figma desktop `60034:1891`,
 * mobil `60042:6929`.
 *
 * Prepínanie kategórií ide cez shadcn `Tabs` a dohrávanie cez
 * `useInfiniteQuery`, rovnako ako na mediatipsk-eshop
 * (`overrides/components/blog/blogs-list.tsx`) — David na ten súbor odkázal
 * ako na vzor pre funkcionalitu.
 *
 * Filter používa verejné typed pole `categories.equals` kontraktu.
 * POZOR: syntax `categories.id IN [...]`, ktorú používa `BlogCards` v jadre,
 * na tomto endpointe vracia prázdno — overené priamo na API.
 *
 * Vzhľad je KAPYCAR: pilulky namiesto klasických tabov (aktívna tmavá, ostatné
 * biele s rámom), prvý článok vo veľkej širokej karte a zvyšok v mriežke.
 *
 * Rozmery odmerané z Figmy:
 *   desktop  medzery medzi blokmi 48 · široká karta 1360×496
 *            mriežka 2 stĺpce po 676 s medzerou 8
 *   mobil    medzery 32 · riadok pilulek sa posúva do strany, nezalamuje sa
 *
 * Farby: jediná premenná je `primary`, zvyšok natvrdo hexom.
 */

const ALL_TAB = 'Všetky články';
/** Figma má na stránke jednu širokú kartu a šesť v mriežke. */
export function BlogsList({
  categories,
  locale,
  initialPage,
}: {
  categories: BlogCategory[];
  locale: string;
  initialPage?: BlogListPage;
}) {
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB);
  const tabsId = useId();
  const panelId = `${tabsId}-panel`;
  const activeTabIndex = [ALL_TAB, ...categories.map(category => category.name)].indexOf(activeTab);
  const activeCategoryId = categories.find(category => category.name === activeTab)?.id;

  const blogsQuery = useInfiniteQuery({
    queryKey: blogListKey(locale, activeCategoryId),
    queryFn: ({ pageParam = 1 }) =>
      fetchBlogs(blogListRequest(locale, pageParam, activeCategoryId)),
    getNextPageParam: nextBlogPage,
    initialPageParam: 1,
    initialData: blogListInitialData(initialPage, activeCategoryId),
    staleTime: 60_000,
    // Keep SSR articles through hydration even when the ISR snapshot is older.
    refetchOnMount: false,
  });

  const blogs: Blog[] = blogsQuery.data?.pages.flatMap(page => page.data) ?? [];
  const [featured, ...rest] = blogs;

  return (
    <Tabs
      value={activeTab}
      defaultValue={ALL_TAB}
      onValueChange={setActiveTab}
      className="flex w-full flex-col gap-8 md:gap-10 lg:gap-12"
    >
      {/* Figma má poradie: široká karta → pilulky → mriežka. Radix ale vyžaduje
          `TabsList` ako súrodenca panela, nie vnútri neho. Preto má panel
          `display: contents` — jeho deti sa stanú priamymi položkami tohto
          flexu a poradie sa doladí cez `order`. Panel tak ostáva jeden, bez
          zdvojených `id` a `aria-controls`. */}
      <TabsList
        className={cn(
          'hide-scroll-bar order-2',
          '-mx-4 flex h-fit w-auto flex-nowrap items-center justify-start gap-2 overflow-x-auto',
          'rounded-none bg-transparent p-0 px-4',
          'lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0',
        )}
      >
        <CategoryTab value={ALL_TAB} id={`${tabsId}-tab-0`} panelId={panelId} />
        {categories.map((category, index) => (
          <CategoryTab key={category.id} value={category.name} id={`${tabsId}-tab-${index + 1}`} panelId={panelId} />
        ))}
      </TabsList>

      <TabsContent
        value={activeTab}
        id={panelId}
        aria-labelledby={activeTabIndex >= 0 ? `${tabsId}-tab-${activeTabIndex}` : undefined}
        className="contents"
      >
        {blogsQuery.isLoading ? (
          <div className="order-1 flex flex-col gap-2">
            {[...Array(3)].map((_, index) => (
              <span
                key={index}
                className="h-[425px] w-full animate-pulse rounded-16 bg-[#E7ECF3] lg:h-[399px]"
              />
            ))}
          </div>
        ) : blogsQuery.error ? (
          <p className="order-1 text-[16px] leading-[1.5] tracking-[-0.031em] text-[#55617A]">
            Články sa nepodarilo načítať. Skúste to prosím o chvíľu znova.
          </p>
        ) : blogs.length === 0 ? (
          <p className="order-3 text-[16px] leading-[1.5] tracking-[-0.031em] text-[#55617A]">
            V tejto kategórii zatiaľ nie sú žiadne články.
          </p>
        ) : (
          <>
            <div className="order-1 flex">
              <ArticleCard blog={featured} featured />
            </div>

            {rest.length > 0 && (
              <div className="order-3 grid grid-cols-1 gap-2 lg:grid-cols-2">
                {/* Oneskorenie po dvojiciach — v mriežke 2×N nabehne vždy celý
                    riadok naraz, nie karta po karte diagonálne. Strop na troch
                    riadkoch: pri dohrávaní ďalších článkov by inak index rástol
                    donekonečna a posledné karty by čakali sekundy. */}
                {rest.map((blog, index) => (
                  <ArticleCard
                    key={blog.id}
                    blog={blog}
                    delay={Math.min(Math.floor(index / 2), 3) * 110}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </TabsContent>

      {blogsQuery.hasNextPage && (
        <Button
          variant="brand-on-dark"
          size="brand"
          rounded="pill"
          onClick={() => blogsQuery.fetchNextPage()}
          isLoading={blogsQuery.isFetchingNextPage}
          className="order-4 w-full lg:mx-auto lg:w-fit"
        >
          Načítať ďalšie články
        </Button>
      )}
    </Tabs>
  );
}

/**
 * Pilulka kategórie. Vo Figme je aktívna tmavá a ostatné biele s rámom —
 * teda varianty Primary a Secondary zo zdieľaného tlačidla. Radix o stave vie
 * cez `data-state`, preto sa farby píšu ako `data-[state=active]:`.
 */
function CategoryTab({ value, id, panelId }: { value: string; id: string; panelId: string }) {
  return (
    <TabsTrigger
      value={value}
      id={id}
      aria-controls={panelId}
      className={cn(
        'label-button shrink-0 whitespace-nowrap rounded-pill px-8',
        'h-[48px] md:h-[52px]',
        'border border-[#DDE3EC] bg-[#FFFFFF] text-[#212121]',
        'transition-colors hover:border-[#212121] hover:bg-[#E7ECF3]',
        'data-[state=active]:border-[#212121] data-[state=active]:bg-[#212121]',
        'data-[state=active]:text-[#FFFFFF] data-[state=active]:shadow-none',
        'ea-blog-category-tab',
      )}
    >
      {value}
    </TabsTrigger>
  );
}
